import { useEffect, useMemo, useState } from 'react';
import { useStoreState, useStoreActions } from '../../../store';
import { PresignedUpload, UploadType } from '../../shared/PresignedUpload';
import { Modal, Button, Card, Form, Slider, Space, Alert, notification } from 'antd';
import { InboxOutlined, DownloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import {
  REQUIRED_SIMULATION_UPLOAD_HEADERS,
  ALLOWED_SIMULATION_UPLOAD_HEADERS,
} from './HistoricalShipmentCSVUploadFixtures';
import { BASE_DETAILS_WORKFLOW_KEY } from './SimulationBaseDetailsStep';
import { SHIPMENT_SELECTION_KEY, UPLOAD_DATASET_MODE } from './SimulationShipmentSelectStep';
import DatePicker from '../../dateFnsComponents/DatePicker';
import { endOfDay, startOfDay, format, subDays, addDays, parse } from 'date-fns';
import type {
  Simulation,
  SimulationSetupData,
  SimulationUpload,
  UploadedShipmentData,
} from '../../../store/simulation/types';
import { validateCsvHeaders } from '../../../store/files/fileUtils';
import { useSearchParams } from 'react-router-dom-v5-compat';
import { SimulationWorkflowCard } from './SimulationWorkflowCard';
import { SIMULATION_ID_SEARCH_PARAM } from '../../../pages/Simulation/SimulationLandingPage';
import { useWatch } from 'antd/lib/form/Form';
import { UploadShipmentTable } from './UploadShipmentTable';
import { SimulationUploadDateValidator } from './UsePreviousHistoricalShipments';
import { getSimulationUploadErrorMessage } from './simulation-utils';
import { useShipmentSizeAlert } from './ShipmentSizeAlert';
import { useSimulationVersion } from '~/hooks/simulation/useSimulationVersion';
import { useUpdateSimulation } from '~/hooks/simulation/useSimulationSetupMutations';
import { useSelectedFileDetails } from '~/hooks/simulation/useSelectedFileDetails';
import { useSession } from '~/hooks/useSession';
import {
  useSimulationUploadTemplateDownload,
  useValidateSimulationUpload,
} from '~/hooks/simulation/useSimulationUploadOps';
import { type UnifiedSimulationUpload, toSimulationSourceLocation } from './uploadAdapter';

const { RangePicker } = DatePicker;

export const UploadHistoricalShipments = () => {
  const updateSimulationStep = useStoreActions((actions) => actions.simulation.setActiveNavigationStep);
  const simulationStep = useStoreState((state) => state.simulation.activeNavigationStep);
  const simulationSetupData = useStoreState((state) => state.simulation.simulationSetupData);
  const setSimulationSetupData = useStoreActions((actions) => actions.simulation.setSimulationSetupData);
  const { mutateAsync: updateSimulation } = useUpdateSimulation();
  const [modal, contextHolder] = Modal.useModal();
  const [selectedFiles, setSelectedFiles] = useState<SimulationUpload[]>([]);
  const [form] = Form.useForm();
  const [dateRangeState, setDateRangeState] = useState<any>([]);
  const [query] = useSearchParams();
  const simulationId = query.get(SIMULATION_ID_SEARCH_PARAM);
  const uploadLoadingState = useState<boolean>(false);
  const samplingPercentage = useWatch('samplingPercentage', form);

  const { version: serviceVersion } = useSimulationVersion(simulationId);
  const { sessionPartnerUrl } = useSession();
  const downloadTemplate = useSimulationUploadTemplateDownload();
  const { validate: validateSimulationUpload } = useValidateSimulationUpload();

  const uploadShipmentData = selectedFiles?.[0];

  useEffect(() => {
    if (simulationId && simulationSetupData[SHIPMENT_SELECTION_KEY]) {
      const { end, start, samplingPercentage, selectedFiles } = simulationSetupData[
        SHIPMENT_SELECTION_KEY
      ] as UploadedShipmentData;
      form.setFieldsValue({
        dateRange: [parse(start, 'yyyy-MM-dd', new Date()), parse(end, 'yyyy-MM-dd', new Date())],
        samplingPercentage: Number(samplingPercentage ?? 100),
      });
      setSelectedFiles(selectedFiles);
    }
  }, [simulationId, simulationSetupData, form]);
  // Display only, and deliberately not what gets saved: a selection persisted by the v2 picker no
  // longer carries the breakdown, and the summary table below reads it. Re-deriving it here keeps
  // the table populated without putting those ~220 KB per file back into simulationSetupData.
  const { files: selectedFilesWithDetails } = useSelectedFileDetails(selectedFiles);

  const selectedShipmentCount = useMemo(() => {
    return (
      selectedFiles?.reduce((prev, val) => {
        return (val?.details?.shipmentCount ?? 0) + prev;
      }, 0) ?? 0
    );
  }, [selectedFiles]);
  const shipmentsToSimulate = Math.floor(selectedShipmentCount * (samplingPercentage / 100));
  const { exceededMaxShipments, ShipmentSizeAlert } = useShipmentSizeAlert({ totalShipments: shipmentsToSimulate });
  const handleSave = async () => {
    if (simulationId) {
      form
        .validateFields()
        .then(async () => {
          const { dateRange, samplingPercentage } = form.getFieldsValue();
          const [start, end] = dateRange;
          const formattedStart = format(startOfDay(start), 'yyyy-MM-dd');
          const formattedEnd = format(endOfDay(end), 'yyyy-MM-dd');
          const simulationData = simulationSetupData[BASE_DETAILS_WORKFLOW_KEY] as Simulation;
          const modifiedSetupData: SimulationSetupData = {
            ...simulationSetupData,
            [SHIPMENT_SELECTION_KEY]: {
              mode: UPLOAD_DATASET_MODE,
              selectedFiles: selectedFiles,
              start: formattedStart,
              end: formattedEnd,
              samplingPercentage,
            },
          };
          setSimulationSetupData(modifiedSetupData);
          await updateSimulation({
            ...simulationData,
            simulationId,
            includesHistoricalSelection: false,
            samplingPercentage,
            historicalShipmentS3Locations: selectedFiles.map((f) => toSimulationSourceLocation(f, serviceVersion)),
            simulationSourceStartDate: format(startOfDay(start), 'yyyy-MM-dd'),
            simulationSourceEndDate: format(endOfDay(end), 'yyyy-MM-dd'),
            simulationSetupData: modifiedSetupData,
          });
          updateSimulationStep(simulationStep + 1);
        })
        .catch((err) => {
          console.log('err', err);
        });
    }
  };
  const disabledDate = (current: any) => {
    // 32 gives us TZ wiggle room for a month idk
    // maybe unnecessary
    if (dateRangeState && !!dateRangeState[1] && subDays(dateRangeState[1], 32) > current) {
      return true;
    }
    if (dateRangeState && !!dateRangeState[0] && addDays(dateRangeState[0], 32) < current) {
      return true;
    }
    return false;
  };
  // v2 uploads validate asynchronously on the Uploads page — no inline upload here.
  if (serviceVersion === 'v2') {
    return (
      <Card>
        <Alert
          message="Upload Simulation Dataset"
          description={
            <>
              Upload and validate your dataset on the{' '}
              <a href={`/${sessionPartnerUrl}/simulation/upload`} target="_blank" rel="noreferrer">
                Uploads page
              </a>{' '}
              (CSV templates are available there). Once its status is Valid, select it here under &quot;Use Previous
              Simulation Dataset&quot;.
            </>
          }
          type="info"
          showIcon
        />
      </Card>
    );
  }

  return (
    <SimulationWorkflowCard
      onSave={handleSave}
      simulationId={simulationId}
      disabled={selectedFiles?.length < 1 || exceededMaxShipments}
    >
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        <Alert
          message="Upload Simulation Dataset"
          description="Provide your own simulation dataset with this method.  The simulation dataset provided must conform to one of the provided CSV templates below."
          type="info"
          showIcon
        />
        <ShipmentSizeAlert />
      </Space>

      {!uploadShipmentData && (
        <>
          <div style={{ marginBottom: 30, marginTop: 10 }}>
            <Space>
              <Button onClick={() => void downloadTemplate(true)} icon={<DownloadOutlined />}>
                Template with all fields
              </Button>
              <Button onClick={() => void downloadTemplate(false)} icon={<DownloadOutlined />}>
                Template with minimal fields
              </Button>
            </Space>
          </div>
          <div>
            <PresignedUpload
              loadingState={uploadLoadingState}
              validate={validateCsvHeaders(REQUIRED_SIMULATION_UPLOAD_HEADERS, ALLOWED_SIMULATION_UPLOAD_HEADERS, true)}
              dragger={true}
              onSuccess={(res) => {
                modal.success({ title: 'Upload successful' });
                validateSimulationUpload(
                  { s3Key: res.s3Key },
                  {
                    onSuccess: (result: UnifiedSimulationUpload) => {
                      if (result.details?.errorType) {
                        const { title, description } = getSimulationUploadErrorMessage(result.details.errorType);
                        notification.open({
                          message: title,
                          description,
                          icon: <ExclamationCircleOutlined style={{ color: 'red' }} />,
                        });
                      } else {
                        setSelectedFiles([result]);

                        if ((result.details?.failureCount ?? 0) > 0) {
                          notification.open({
                            message: 'Unprocessable Rows Detected',
                            description: `${result.details?.failureCount} rows of ${result.details?.shipmentCount} could not be processed and will be ignored for this simulation`,
                            icon: <ExclamationCircleOutlined style={{ color: 'blue' }} />,
                          });
                        }

                        if (
                          !form.getFieldValue('dateRange') &&
                          result.details?.minShipDate &&
                          result.details?.maxShipDate
                        ) {
                          // Simulation start and end date are stored as plain dates in the simulation db.
                          // The AntD date picker defaults to the local timezone.
                          // Passing UTC dates through the DateTimePicker can cause an off by one error because
                          // simulation_start_date and simulation_end_date are interpreted as UTC dates by the simulation batcher.
                          // This small hack is inelegant but it works.
                          const startDate = new Date(result.details.minShipDate.replace('Z', ''));
                          const endDate = new Date(result.details.maxShipDate.replace('Z', ''));
                          form.setFieldValue('dateRange', [startDate, endDate]);
                        }
                      }
                    },
                    onError: (err: unknown) => {
                      modal.error({
                        title: 'Upload failed',
                        content: err instanceof Error ? err.message : 'An unknown error occurred',
                      });
                    },
                  },
                );
              }}
              onFailure={(err) => {
                modal.error({
                  title: 'Upload failed',
                  content: err.message,
                });
              }}
              uploadType={UploadType.SIMULATION}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag a valid simulation dataset this area to upload</p>
            </PresignedUpload>
          </div>
        </>
      )}
      {selectedFiles?.length > 0 && (
        <div style={{ marginTop: 30 }}>
          <Form form={form}>
            <Space size={100}>
              <Form.Item
                required={true}
                rules={[{ required: true }, { validator: SimulationUploadDateValidator }]}
                name={'dateRange'}
                tooltip={
                  'For this simulation we will only run shipments that fall within this date range, even if the files include shipments that fall outside of this range'
                }
                label={'Select a date range for this simulation'}
              >
                <RangePicker
                  onCalendarChange={(dates) => {
                    setDateRangeState(dates);
                  }}
                  disabledDate={disabledDate}
                  showTime={false}
                />
              </Form.Item>
              <Form.Item
                initialValue={100}
                required={true}
                style={{ marginBottom: 0 }}
                name="samplingPercentage"
                extra={`${shipmentsToSimulate.toLocaleString()} shipments to simulate`}
                tooltip={
                  'Use the sampling percentage to reduce the number of shipments to run for your simulation. This will help you get your results faster'
                }
                label="Shipment Sampling Percentage"
              >
                <Slider
                  tooltip={{ open: true, placement: 'right' }}
                  defaultValue={100}
                  min={1}
                  style={{ width: 200 }}
                />
              </Form.Item>
            </Space>
          </Form>
          <Alert
            type="success"
            showIcon={true}
            message={`${selectedFiles?.length} dataset file${selectedFiles?.length === 1 ? '' : 's'} uploaded`}
            description="Verify the start and end date and sampling percentage to be used."
            action={
              <Button
                type={'primary'}
                danger
                onClick={() => {
                  setSelectedFiles([]);
                }}
              >
                Reset Upload
              </Button>
            }
          />
          <UploadShipmentTable data={selectedFilesWithDetails} samplingPercentage={samplingPercentage} />
        </div>
      )}
      {contextHolder}
    </SimulationWorkflowCard>
  );
};
